import { makeStyles } from "tss-react/mui"
import { SelectChangeEvent, Stack } from "@mui/material"

import { useApiQuery, usePublicApiQuery } from "../../../../../../core/api/useApiQuery"
import { PackageSlipSkeleton } from "./slipModal/PackageSlipSkeleton"
import { Box } from "@mui/system"
import {
    fetchMeetingServiceOrdersForExport,
    MeetingServiceOrdersForExportResponse,
    MerchantMeetingServiceOrdersQuery,
} from "../meetingServiceApi"
import SingleOrder from "./slipModal/SingleOrder"
import { Fragment, useMemo, useState } from "react"
import { Link, useParams } from "react-router-dom"
import {
    fetchOrderDetails,
    fetchPublicOrderDetails,
    MeetingServiceQuery,
    OrderDetailsResponse,
} from "../../../../../nonAdminUser/reports/home/meetingServiceApi"
import { useAppSelector } from "../../../../../store/storeHooks"
import { selectEffectiveUserId } from "../../../../../context/contextSelectors"
import { getPublicPackageSlipReceiptRoute, LOGIN_ROUTE } from "../../../../../../constants/routes"
import { SharePrintButtons } from "../../../../../nonAdminUser/reports/MeetingsReceipt/components/SharePrintButtons"

import { StandardDialog } from "../../../../../../shared/components/dialogs/StandardDialog"
import { ErrorView } from "../../../../../../shared/components/errors/ErrorView"
import GeneralErrorAnim from "../../../../../../shared/assets/general-error-lottie-anim.json"
import FacilitynetLogo from "../../../../../../shared/layouts/assets/facilit-net-logo.svg"
import { MeetingServiceOrder } from "../meetingServiceTypes"
import { CalendarEvent } from "../../../../../../shared/components/calendar/calendarTypes"
import { useEnvironment } from "../../../../../environment/envHooks"
import { ReceiptAndSlipDialogHeader } from "../../../components/receiptAndSlip/ReceiptAndSlipDialogHeader"
import { ReceiptNavigation } from "../../../../../../shared/components/receipt/ReceiptNavigation"
import { OrderChatHistoryPage } from "../../../../../../shared/components/orderChat/print/OrderChatHistoryPage"
import { OrderChatStatusCard } from "../../../../../../shared/components/orderChat/OrderChatStatusCard"
import { OrderChatDrawer } from "../../../../../../shared/components/orderChat/OrderChatDrawer"
import { ChatMessageAuthorRole, UserType } from "../../../../../../shared/components/orderChat/orderChatTypes"
import type { ChatOrder } from "../../../../../../shared/components/orderChat/orderChatTypes"
import { HIDDEN_IN_PRINT, VISIBLE_ONLY_IN_PRINT } from "../../../../../../shared/components/print/printPageOnly"
import { usePrintWithChatHistory } from "../../../../../../shared/components/orderChat/print/usePrintWithChatHistory"
import type { RenderPrintViewOrderMenu } from "../../../components/receiptAndSlip/PrintViewOrderActions"

const useStyles = makeStyles()((theme) => ({
    slipList: {
        "@media print": {
            display: "block",
        },
    },
    hiddenInPrint: {
        ...HIDDEN_IN_PRINT,
    },
    visibleOnlyInPrint: {
        ...VISIBLE_ONLY_IN_PRINT,
    },
    container: {
        backgroundColor: theme.palette.tailwind.slate[100],
        minHeight: "100vh",
        gap: "3.5rem",
        paddingBottom: "3.5rem",
        justifyContent: "start",
        [theme.breakpoints.down("sm")]: {
            backgroundColor: theme.palette.common.white,
            gap: 0,
        },
        "@media print": {
            backgroundColor: "transparent",
            minHeight: "auto",
            width: "100%",
            paddingBottom: 0,
        },
    },
    slideContainer: {
        flex: 1,
        display: "flex",
        justifyContent: "center",
        marginTop: "1.5rem",
        [theme.breakpoints.down("sm")]: {
            marginTop: 0,
        },
        "@media print": {
            marginTop: 0,
        },
    },
    slide: {
        width: "80%",
        "@media print": {
            width: "100%",
            display: "block",
        },
    },
    header: {
        display: "grid",
        gridTemplateColumns: "minmax(0,1fr) auto minmax(0,1fr)", // L | C | R
        justifyContent: "space-between",
        alignItems: "center",
        padding: "1.5rem",
        position: "sticky",
        top: 0,
        zIndex: 1,
        backgroundColor: "white",
        boxShadow: "0px 2px 6px 0px #00000014",
        "@media print": {
            display: "none",
        },
        [theme.breakpoints.down("sm")]: {
            gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
            padding: "0 1.5rem",
            zIndex: 1,
            boxShadow: "0px 3px 1px -2px #00000033,  0px 2px 2px 0px #00000024, 0px 1px 5px 0px #0000001F",
        },
    },
    logo: {
        width: "auto",
    },
    logoLink: {
        display: "flex",
        alignItems: "center",
        [theme.breakpoints.up("sm")]: {
            display: "none",
        },
    },
    actionButtons: {
        justifySelf: "end",
    },
}))

export type AnyMeetingServiceOrder = MeetingServiceOrder | CalendarEvent

type StartPageLayoutProps = Readonly<{
    id?: number
    merchantId?: number
    onClose?: () => void
    orderIds?: number[]
    open?: boolean
    orders?: AnyMeetingServiceOrder[]
    renderOrderMenu?: RenderPrintViewOrderMenu
}>

const transformOrdersForExport = (data: MeetingServiceOrdersForExportResponse) => data?.orders
const transformData = (data: OrderDetailsResponse) => (data?.orders.length ? data?.orders[0] : undefined)

const getIndexOfPreviewedOrder = (orders?: AnyMeetingServiceOrder[], id?: number | string) => {
    if (!id || !orders?.length) return 0
    return orders.findIndex((o) => o.id === id)
}

export function OrderPackageSlip({
    id,
    open = false,
    merchantId,
    orderIds,
    orders = [],
    onClose = () => {},
    renderOrderMenu,
}: StartPageLayoutProps) {
    const { classes, cx } = useStyles()
    const { slipUid: uid } = useParams()
    const [currentMeetingIndex, setCurrentMeetingIndex] = useState<number>(getIndexOfPreviewedOrder(orders, id))
    const { currentEnv } = useEnvironment()

    const userId = useAppSelector(selectEffectiveUserId) || 0
    const activeOrder = useMemo(() => orders?.[currentMeetingIndex], [orders, currentMeetingIndex])
    const isAuthenticated = Boolean(userId)
    const isPublic = Boolean(uid)
    const isSelectMode = Boolean(orderIds?.length)

    const {
        data: exportOrders = [],
        isFetching: isFetchingOrdersForExport,
        error: isFetchingOrdersForExportError,
    } = useApiQuery(fetchMeetingServiceOrdersForExport, {
        queryName: MerchantMeetingServiceOrdersQuery.ORDERS_FOR_EXPORT,
        dependencies: {
            merchantId: merchantId ?? 0,
            orderIds: orderIds,
        },
        enabled: isAuthenticated && Boolean(orderIds?.length),
        select: transformOrdersForExport,
    })

    const {
        data: orderDetailsPublic,
        isFetching: orderDetailsPublicLoading,
        error: orderDetailsPublicError,
    } = usePublicApiQuery(fetchPublicOrderDetails, {
        queryName: MeetingServiceQuery.PUBLIC_ORDER_DETAILS,
        dependencies: {
            orderUid: uid ?? "",
        },
        enabled: Boolean(uid) && isPublic && !isSelectMode,
        select: transformData,
    })

    const {
        data: orderDetailsAuth,
        isInitialLoading: orderDetailsAuthLoading,
        isError: orderDetailsAuthError,
    } = useApiQuery(fetchOrderDetails, {
        queryName: MeetingServiceQuery.ORDER_DETAILS,
        dependencies: {
            orderId: Number(activeOrder?.id) || 0,
        },
        enabled: Boolean(activeOrder?.id) && !isPublic && !isSelectMode,
        select: transformData,
    })

    const goToPreviousOrder = () => {
        if (currentMeetingIndex > 0) {
            const prevIndex = currentMeetingIndex - 1
            setCurrentMeetingIndex(prevIndex)
        }
    }

    const goToNextOrder = () => {
        if (currentMeetingIndex < orders.length - 1) {
            const nextIndex = currentMeetingIndex + 1
            setCurrentMeetingIndex(nextIndex)
        }
    }

    const handleOrderChange = (event: SelectChangeEvent<number | string>) => {
        const index = getIndexOfPreviewedOrder(orders, event.target.value)
        setCurrentMeetingIndex(index)
    }

    const orderDetails = orderDetailsAuth ?? orderDetailsPublic
    const isLoading = orderDetailsPublicLoading || orderDetailsAuthLoading || isFetchingOrdersForExport
    const isError = orderDetailsPublicError || orderDetailsAuthError || isFetchingOrdersForExportError

    const printedOrderIds = (isSelectMode ? exportOrders : [orderDetails]).flatMap((order) => (order ? [order.id] : []))
    const { isPrintChatEnabled, isChatHistoryPrinted, printedSingleOrder, getOrderPrintActions, printWithChatHistory } =
        usePrintWithChatHistory(printedOrderIds)

    const [chatOrder, setChatOrder] = useState<ChatOrder>()
    const isChatVisible = isPrintChatEnabled && !isPublic

    const skeletonCount = isSelectMode ? (orderIds?.length ?? 1) : 1

    const getListOrderMessages = (orderId: number) => {
        const listOrder = orders.find((order) => order.id === orderId)
        return listOrder && "messages" in listOrder ? listOrder.messages : undefined
    }

    const renderChatStatus = (orderId: number, title: string | undefined, hasChat?: boolean) =>
        isChatVisible && (
            <OrderChatStatusCard
                orderId={orderId}
                viewerRole={ChatMessageAuthorRole.MERCHANT}
                hasChat={hasChat}
                orderMessages={getListOrderMessages(orderId)}
                onClick={() => setChatOrder({ id: orderId, title })}
            />
        )

    const renderSlipContent = () => {
        if (isLoading) {
            return Array.from({ length: skeletonCount }).map((_, index) => <PackageSlipSkeleton key={index} />)
        }

        if (isSelectMode) {
            const printedOrder = exportOrders.find((order) => order.id === printedSingleOrder?.orderId)

            return (
                <>
                    <Stack spacing="2rem" className={cx(classes.slipList, printedOrder && classes.hiddenInPrint)}>
                        {exportOrders.map((order) => (
                            <Fragment key={order.id}>
                                <SingleOrder
                                    order={order}
                                    chatStatus={renderChatStatus(
                                        order.id,
                                        order.meetingDetails?.meetingName,
                                        Boolean(order.messages?.length),
                                    )}
                                    orderMenu={renderOrderMenu?.(order.id, getOrderPrintActions(order.id))}
                                />
                                {isChatHistoryPrinted && <OrderChatHistoryPage orderId={order.id} />}
                            </Fragment>
                        ))}
                    </Stack>
                    {printedOrder && (
                        <Box className={classes.visibleOnlyInPrint}>
                            <SingleOrder order={printedOrder} />
                            {printedSingleOrder?.withChatHistory && <OrderChatHistoryPage orderId={printedOrder.id} />}
                        </Box>
                    )}
                </>
            )
        }

        if (orderDetails) {
            return (
                <>
                    <SingleOrder
                        order={orderDetails}
                        chatStatus={renderChatStatus(orderDetails.id, orderDetails.meetingDetails?.meetingName)}
                        orderMenu={renderOrderMenu?.(orderDetails.id)}
                    />
                    {isChatHistoryPrinted && <OrderChatHistoryPage orderId={orderDetails.id} />}
                </>
            )
        }

        return null
    }

    const content = isError ? (
        <ErrorView
            lottieAnimation={GeneralErrorAnim}
            title="A problem occurred"
            message="Failed to load"
            buttonLabel="Close"
            onClose={onClose}
        />
    ) : (
        <Stack direction="column" className={classes.container}>
            <Box className={classes.header}>
                <ReceiptAndSlipDialogHeader
                    handleOrderChange={handleOrderChange}
                    id={Number(activeOrder?.id)}
                    uid={uid}
                    isAuthenticated={isAuthenticated}
                    isSelectMode={isSelectMode}
                    orderIdsLength={orderIds?.length}
                    orders={orders}
                    orderDetails={orderDetails}
                    variant="slip"
                />
                <Link className={classes.logoLink} to={LOGIN_ROUTE}>
                    <img className={classes.logo} src={FacilitynetLogo} alt="Facilitynet" />
                </Link>

                <ReceiptNavigation
                    isAuthenticated={isAuthenticated}
                    isSelectMode={isSelectMode}
                    goToPreviousOrder={goToPreviousOrder}
                    goToNextOrder={goToNextOrder}
                    ordersLength={orders.length}
                    currentMeetingIndex={currentMeetingIndex}
                    variant="slip"
                />

                <Box className={classes.actionButtons}>
                    <SharePrintButtons
                        onPrintWithChatHistory={isPublic ? undefined : printWithChatHistory}
                        onClose={onClose}
                        hasShare={!isSelectMode && !isPublic}
                        hasClose={true}
                        copyUrl={`${currentEnv?.webapp?.baseUrl}${getPublicPackageSlipReceiptRoute(orderDetails?.uid)}`}
                    />
                </Box>
            </Box>
            <Box id="printable-content" className={classes.slideContainer}>
                <Stack direction="column" spacing="2rem" className={classes.slide}>
                    {renderSlipContent()}
                </Stack>
            </Box>

            {chatOrder && (
                <OrderChatDrawer
                    open
                    raised
                    onClose={() => setChatOrder(undefined)}
                    orderId={chatOrder.id}
                    orderTitle={chatOrder.title}
                    viewerRole={ChatMessageAuthorRole.MERCHANT}
                    sender={userId && merchantId ? { type: UserType.MERCHANT, merchantId, userId } : undefined}
                />
            )}
        </Stack>
    )

    return isPublic ? (
        content
    ) : (
        <StandardDialog open={open} onClose={onClose} isFullScreen hideCloseIcon topbar={<></>}>
            {content}
        </StandardDialog>
    )
}
